import React, { useEffect, useRef, useState } from "react";
import ReactSelect from "react-select";
import {
  Filter,
  Ruler,
  Building2,
  ArrowUpDown,
  ListFilter,
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
} from "lucide-react";
import { useForm, Controller } from "react-hook-form";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import {
  fetchPiecesStock,
  fetchStockSizes,
  fetchStockParties,
  getPiecesStock,
  getTotalPiecesStock,
  getTotalPiecesInches,
  getPiecesStockFilter,
  setPiecesStockFilter,
  getStockSizeOptions,
  getStockPartyOptions,
  getStockLoading,
} from "../../features/stock/stockSlice";
import { getSelectedBranch } from "../../features/settings/settingsSlice";
import { filterSelectStyles, toastStyles } from "../../static/styles";
import { exportModuleReport } from "../../services/reportExport.service";
import { t } from "../../locales";
import { formatSizeLabel, formatSize } from "../../lib/utils";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Button } from "../ui/button";
import {
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
} from "../ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import { PiecesStockFilter, PieceStockRow } from "../../types/stock";

const formatDate = (val: string | null | undefined): string => {
  if (!val) return "-";
  const d = new Date(val);
  if (isNaN(d.getTime())) return String(val);
  const dd = String(d.getDate()).padStart(2, "0");
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const yy = d.getFullYear();
  return `${dd}-${mm}-${yy}`;
};

const PiecesStockList = () => {
  const dispatch = useAppDispatch();

  const rows = useAppSelector(getPiecesStock);
  const totalRows = useAppSelector(getTotalPiecesStock);
  const totalInches = useAppSelector(getTotalPiecesInches);
  const filter = useAppSelector(getPiecesStockFilter);
  const loading = useAppSelector(getStockLoading);
  const selectedBranch = useAppSelector(getSelectedBranch);
  const sizeOptions = useAppSelector(getStockSizeOptions);
  const partyOptions = useAppSelector(getStockPartyOptions);

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [showFilters, setShowFilters] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [selectedFilterSize, setSelectedFilterSize] = useState<{ value: number; label: string } | null>(null);
  const [selectedFilterParty, setSelectedFilterParty] = useState<{ value: number; label: string } | null>(null);

  const sortByOptions = [
    { value: "party_name", label: t.stock.sortBy.partyName },
    { value: "material_name", label: t.stock.sortBy.sizeName },
    { value: "remaining_inches", label: t.stock.sortBy.remainingInches },
    { value: "initial_inches", label: t.stock.sortBy.initialInches },
  ];

  const sortOrderOptions = [
    { value: "asc", label: t.stock.sortOrderOptions.asc },
    { value: "desc", label: t.stock.sortOrderOptions.desc },
  ];

  const { control, handleSubmit, reset, getValues } = useForm<PiecesStockFilter>({
    defaultValues: { ...filter },
  });

  const doFetch = () => {
    dispatch(
      fetchPiecesStock({
        page,
        size: pageSize,
        size_id: filter.size_id,
        party_id: filter.party_id,
        sortBy: filter.sortBy,
        sortOrder: filter.sortOrder,
      })
    );
  };

  const prevBranchIdRef = useRef<number | null>(null);
  const selectedBranchId = selectedBranch?.id ?? null;

  useEffect(() => {
    if (!selectedBranchId) return;
    const prevBranchId = prevBranchIdRef.current;
    prevBranchIdRef.current = selectedBranchId;
    const isBranchChange =
      prevBranchId !== null && prevBranchId !== selectedBranchId;
    if (isBranchChange && page !== 1) {
      setPage(1);
      return;
    }
    doFetch();
  }, [page, pageSize, filter, selectedBranchId]);

  useEffect(() => {
    dispatch(fetchStockSizes());
    dispatch(fetchStockParties());
  }, [dispatch]);

  useEffect(() => {
    if (filter.size_id && sizeOptions.length > 0) {
      setSelectedFilterSize(sizeOptions.find((o) => o.value === filter.size_id) || null);
    } else {
      setSelectedFilterSize(null);
    }
  }, [filter.size_id, sizeOptions]);

  useEffect(() => {
    if (filter.party_id && partyOptions.length > 0) {
      setSelectedFilterParty(partyOptions.find((o) => o.value === filter.party_id) || null);
    } else {
      setSelectedFilterParty(null);
    }
  }, [filter.party_id, partyOptions]);

  const onApplyFilters = (data: PiecesStockFilter) => {
    dispatch(
      setPiecesStockFilter({
        size_id: selectedFilterSize?.value || null,
        party_id: selectedFilterParty?.value || null,
        sortBy: data.sortBy,
        sortOrder: data.sortOrder,
      })
    );
    setPage(1);
  };

  const onExportReport = () => {
    const values = getValues();
    exportModuleReport(
      "/v1/stock/pieces/report-pdf",
      {
        size_id: selectedFilterSize?.value || null,
        party_id: selectedFilterParty?.value || null,
        sortBy: values.sortBy,
        sortOrder: values.sortOrder,
      },
      toastStyles,
      setIsExporting,
      undefined,
      { requireDateRange: false }
    );
  };

  const handleClearFilters = () => {
    const defaults: PiecesStockFilter = {
      size_id: null,
      party_id: null,
      sortBy: "party_name",
      sortOrder: "asc",
    };
    reset(defaults);
    setSelectedFilterSize(null);
    setSelectedFilterParty(null);
    dispatch(setPiecesStockFilter(defaults));
    setPage(1);
  };

  const totalPages = Math.ceil(totalRows / pageSize) || 1;

  const getVisiblePages = (): (number | string)[] => {
    const pages: (number | string)[] = [];
    if (totalPages <= 7) {
      for (let i = 1; i <= totalPages; i++) pages.push(i);
    } else {
      pages.push(1);
      if (page > 3) pages.push("...");
      for (let i = Math.max(2, page - 1); i <= Math.min(totalPages - 1, page + 1); i++) {
        pages.push(i);
      }
      if (page < totalPages - 2) pages.push("...");
      pages.push(totalPages);
    }
    return pages;
  };

  const start = totalRows === 0 ? 0 : (page - 1) * pageSize + 1;
  const end = Math.min(page * pageSize, totalRows);

  return (
    <div className="space-y-4">
      {/* Top bar */}
      <div className="flex flex-wrap items-center gap-3 justify-between">
        <div className="text-sm text-gray-700">
          <span className="font-semibold text-[#113F67]">{t.stock.totalPiecesLabel}:</span>{" "}
          <span className="font-semibold">{totalRows}</span>
          <span className="mx-2 text-gray-400">|</span>
          <span className="font-semibold text-[#113F67]">{t.stock.totalSizeLabel}:</span>{" "}
          <span className="font-semibold">{formatSize(totalInches)}</span>
        </div>
        <Button
          variant="outline"
          className="h-10 px-5 rounded-lg border-gray-300 text-[#113F67] hover:bg-gray-50 hover:text-[#113F67]"
          onClick={() => setShowFilters(!showFilters)}
        >
          <Filter className="h-4 w-4 mr-2" />
          {t.stock.filterToggle}
        </Button>
      </div>

      {/* Filters */}
      {showFilters && (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">{t.stock.filterTitle}</CardTitle>
          </CardHeader>
          <CardContent className="pt-0 pb-4">
            <form onSubmit={handleSubmit(onApplyFilters)}>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 items-end">
                <div>
                  <label className="text-sm font-semibold text-[#113F67] mb-1 block">
                    {t.stock.sizeLabel}
                  </label>
                  <div className="relative">
                    <Ruler className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-[#34699A] z-10 pointer-events-none" />
                    <ReactSelect
                      styles={filterSelectStyles}
                      options={sizeOptions}
                      isClearable
                      placeholder={t.stock.selectSize}
                      value={selectedFilterSize}
                      onChange={(opt: any) => setSelectedFilterSize(opt || null)}
                    />
                  </div>
                </div>

                <div>
                  <label className="text-sm font-semibold text-[#113F67] mb-1 block">
                    {t.stock.partyLabel}
                  </label>
                  <div className="relative">
                    <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-[#34699A] z-10 pointer-events-none" />
                    <ReactSelect
                      styles={filterSelectStyles}
                      options={partyOptions}
                      isClearable
                      placeholder={t.stock.selectParty}
                      value={selectedFilterParty}
                      onChange={(opt: any) => setSelectedFilterParty(opt || null)}
                    />
                  </div>
                </div>

                <div>
                  <label className="text-sm font-semibold text-[#113F67] mb-1 block">
                    {t.stock.sortByLabel}
                  </label>
                  <div className="relative">
                    <ListFilter className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-[#34699A] z-10 pointer-events-none" />
                    <Controller
                      name="sortBy"
                      control={control}
                      render={({ field }) => (
                        <ReactSelect
                          styles={filterSelectStyles}
                          options={sortByOptions}
                          value={sortByOptions.find((o) => o.value === field.value)}
                          onChange={(opt: any) => field.onChange(opt?.value || "party_name")}
                        />
                      )}
                    />
                  </div>
                </div>

                <div>
                  <label className="text-sm font-semibold text-[#113F67] mb-1 block">
                    {t.stock.sortOrderLabel}
                  </label>
                  <div className="relative">
                    <ArrowUpDown className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-[#34699A] z-10 pointer-events-none" />
                    <Controller
                      name="sortOrder"
                      control={control}
                      render={({ field }) => (
                        <ReactSelect
                          styles={filterSelectStyles}
                          options={sortOrderOptions}
                          value={sortOrderOptions.find((o) => o.value === field.value)}
                          onChange={(opt: any) => field.onChange(opt?.value || "desc")}
                        />
                      )}
                    />
                  </div>
                </div>
              </div>

              <div className="flex gap-3 mt-4 border-t pt-4 justify-end">
                <Button
                  type="button"
                  variant="outline"
                  className="h-10 px-5 rounded-lg border-gray-300 text-[#113F67] hover:bg-gray-50 hover:text-[#113F67]"
                  onClick={handleClearFilters}
                >
                  {t.stock.clearFilters}
                </Button>
                <Button
                  type="button"
                  disabled={isExporting}
                  onClick={onExportReport}
                  className="h-10 px-5 rounded-lg bg-[#113F67] hover:bg-[#0d2f4f] border border-[#113F67] text-white"
                >
                  {isExporting ? "..." : t.stock.exportReport}
                </Button>
                <Button
                  type="submit"
                  className="h-10 px-5 rounded-lg bg-[#113F67] hover:bg-[#0d2f4f] border border-[#113F67]"
                >
                  {t.stock.applyFilters}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      )}

      {/* Mobile cards */}
      <div className="md:hidden space-y-3">
        {loading && rows.length === 0 ? (
          <Card>
            <CardContent className="py-8">
              <div className="flex items-center justify-center gap-2 text-gray-500">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#113F67]" />
                {t.stock.loading}
              </div>
            </CardContent>
          </Card>
        ) : !loading && rows.length === 0 ? (
          <Card>
            <CardContent className="py-8 text-center text-gray-500">
              {t.stock.noRecords}
            </CardContent>
          </Card>
        ) : (
          <div className={`space-y-3${loading ? " opacity-50 pointer-events-none" : ""}`}>
            {rows.map((row: PieceStockRow, index: number) => (
              <Card key={`piece-${row.id}`}>
                <CardContent className="p-4">
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <span className="font-medium text-gray-900">
                        {row.party_firm_name || row.party_name || "-"}
                      </span>
                      {row.party_firm_name && row.party_name && (
                        <div className="text-sm text-gray-500">{row.party_name}</div>
                      )}
                      <div className="text-xs text-gray-500 mt-0.5">
                        #{(page - 1) * pageSize + index + 1}
                      </div>
                    </div>
                  </div>
                  <div className="mt-3 pt-3 border-t space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-gray-500">{t.stock.sizeLabel}</span>
                      <span className="text-gray-700 font-medium">{formatSizeLabel(row)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-500">{t.stock.remainingInchesLabel}</span>
                      <span className="font-semibold text-[#113F67]">
                        {formatSize(row.remaining_inches)}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-500">{t.stock.sourceOrderLabel}</span>
                      <span className="text-gray-700">
                        {row.source_order_id ? (
                          <>
                            #{row.source_order_id}
                            {row.source_order_date && (
                              <span className="text-gray-500"> · {formatDate(row.source_order_date)}</span>
                            )}
                          </>
                        ) : (
                          "-"
                        )}
                      </span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>

      {/* Table */}
      <Card className="hidden md:block">
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-16">{t.stock.table.srNo}</TableHead>
                  <TableHead>{t.stock.partyLabel}</TableHead>
                  <TableHead>{t.stock.sizeLabel}</TableHead>
                  <TableHead className="text-right">{t.stock.remainingInchesLabel}</TableHead>
                  <TableHead>{t.stock.sourceOrderLabel}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={5} className="text-center py-8">
                      <div className="flex items-center justify-center gap-2 text-gray-500">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#113F67]" />
                        {t.stock.loading}
                      </div>
                    </TableCell>
                  </TableRow>
                ) : rows.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5} className="text-center py-8 text-gray-500">
                      {t.stock.noRecords}
                    </TableCell>
                  </TableRow>
                ) : (
                  rows.map((row: PieceStockRow, index: number) => (
                    <TableRow className="align-top" key={`piece-${row.id}`}>
                      <TableCell className="font-medium text-gray-500">
                        {(page - 1) * pageSize + index + 1}
                      </TableCell>
                      <TableCell>
                        <div className="text-gray-900">{row.party_firm_name || row.party_name || "-"}</div>
                        {row.party_firm_name && row.party_name && (
                          <div className="text-xs text-gray-500">{row.party_name}</div>
                        )}
                      </TableCell>
                      <TableCell>
                        <span className="font-medium text-gray-900">{formatSizeLabel(row)}</span>
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="font-semibold text-[#113F67]">
                          {formatSize(row.remaining_inches)}
                        </div>
                      </TableCell>
                      <TableCell>
                        {row.source_order_id ? (
                          <>
                            <div className="font-medium text-gray-900">
                              #{row.source_order_id}
                            </div>
                            {row.source_order_date && (
                              <div className="text-xs text-gray-500">
                                {formatDate(row.source_order_date)}
                              </div>
                            )}
                          </>
                        ) : (
                          "-"
                        )}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      {/* Pagination */}
      {!loading && rows.length > 0 && (
        <div className="flex flex-wrap items-center justify-end md:justify-between gap-4">
          <div className="hidden md:flex items-center gap-2 text-sm text-gray-600">
            <span>
              {start}-{end} of {totalRows} {t.stock.pageOfLabel}
            </span>
            <span>{t.stock.pageSizeLabel}</span>
            <Select
              value={String(pageSize)}
              onValueChange={(val) => {
                setPageSize(Number(val));
                setPage(1);
              }}
            >
              <SelectTrigger className="w-20 h-8">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="10">10</SelectItem>
                <SelectItem value="25">25</SelectItem>
                <SelectItem value="50">50</SelectItem>
                <SelectItem value="100">100</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="flex items-center gap-1">
            <Button
              variant="outline"
              className="h-8 w-8 p-0"
              onClick={() => setPage(1)}
              disabled={page === 1}
            >
              <ChevronsLeft className="h-4 w-4" />
            </Button>
            <Button
              variant="outline"
              className="h-8 w-8 p-0"
              onClick={() => setPage(Math.max(1, page - 1))}
              disabled={page === 1}
            >
              <ChevronLeft className="h-4 w-4" />
            </Button>
            {getVisiblePages().map((p, i) =>
              typeof p === "string" ? (
                <span key={`ellipsis-${i}`} className="px-2 text-gray-400">
                  ...
                </span>
              ) : (
                <Button
                  key={p}
                  variant={page === p ? "default" : "outline"}
                  className={`w-8 h-8 p-0 ${
                    page === p ? "bg-[#113F67] hover:bg-[#0d2f4d]" : ""
                  }`}
                  onClick={() => setPage(p)}
                >
                  {p}
                </Button>
              )
            )}
            <Button
              variant="outline"
              className="h-8 w-8 p-0"
              onClick={() => setPage(Math.min(totalPages, page + 1))}
              disabled={page === totalPages}
            >
              <ChevronRight className="h-4 w-4" />
            </Button>
            <Button
              variant="outline"
              className="h-8 w-8 p-0"
              onClick={() => setPage(totalPages)}
              disabled={page === totalPages}
            >
              <ChevronsRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default PiecesStockList;
